import React, { useState, useEffect, useRef } from "react";
import moment, { Moment } from "moment";
import { Modal, Row } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../..";
import {
  characteristicsActions,
  fetchCharacteristicDic,
  fetchCharacteristicsUnitsDic,
  fetchObjectCharTypes,
  fetchObjectTypes,
} from "../../../store/slice/characteristicsSlice";
import { checkIsLoaded, toHttpFormatStartOfDay } from "../../../../../lib/utils";
import { TableDataCharacteristic } from "../../../features/CharacteristicsTable/CharacteristicsTableTypes";
import { FieldsError } from "./useCharacteristicsInitialisationType";

type TData = TableDataCharacteristic[];

interface IProps {
  initialFromDate?: Moment | string | null;
}

export const useCharacteristicsInitialisation = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { initialFromDate } = props;

  // Контроль открытия строк таблицы (для добавления новой версии)
  const [expandedRowKeysCharacteristic, setExpandedRowKeysCharacteristic] = useState<string[]>([]);

  const onChangeExpandedRowKeysCharacteristic = (key: string | string[]) => {
    if (Array.isArray(key)) {
      setExpandedRowKeysCharacteristic(key);
    } else {
      setExpandedRowKeysCharacteristic((prev) => {
        const keysSet = new Set(prev);
        keysSet.has(key) ? keysSet.delete(key) : keysSet.add(key);
        return Array.from(keysSet);
      });
    }
  };

  // Характеристики
  const [infoCharacteristic, setInfoCharacteristic] = useState<TData>([]);
  const [charValuesCharacteristic, setCharValuesCharacteristic] = useState<TData>([]);
  const [productCharValuesCharacteristic, setProductCharValuesCharacteristic] = useState<TData>([]);

  const allTableData = [
    ...infoCharacteristic,
    ...charValuesCharacteristic,
    ...productCharValuesCharacteristic,
  ];

  const allTableDataObject = {
    infoCharacteristic,
    charValuesCharacteristic,
    productCharValuesCharacteristic,
  };

  // Очистка данных таблицы
  const clearAllTableData = () => {
    setInfoCharacteristic([]);
    setCharValuesCharacteristic([]);
    setProductCharValuesCharacteristic([]);
  };

  const {
    objectCharTypes,
    objectTypesLoading,
    objectCharTypesFromDate,
    prevFromDate,
    characteristicsUnitsDicLoading,
  } = useAppSelector((state) => state.characteristics);

  const [isLoading, setIsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState<FieldsError>({});

  // Функция для проверки кардинальности характеристики
  const getIsValidCardinality = (
    record: TableDataCharacteristic,
    characteristicsTable: TableDataCharacteristic[]
  ): boolean => {
    // Смотрим сколько таких характеристик во всей таблице
    let findCharAmount = 0;
    characteristicsTable.forEach((el) => {
      if (record.characteristic?.id === el.characteristic?.id) {
        if (record.characteristic?.charType?.specificDicTypeId) {
          if (
            record.specificDicId &&
            el.specificDicId &&
            el.specificDicId === record.specificDicId
          ) {
            findCharAmount += 1;
          }
        } else {
          findCharAmount += 1;
        }
      }
    });
    let isInvalidCardinality = false;

    // Указываем что кардинальность превышена
    if (
      record.characteristic?.maxCardinality &&
      findCharAmount > record.characteristic?.maxCardinality
    ) {
      isInvalidCardinality = true;
    }

    if (
      record.characteristic?.minCardinality &&
      findCharAmount < record.characteristic?.minCardinality
    ) {
      isInvalidCardinality = true;
    }

    return isInvalidCardinality;
  };

  const onCheckFieldsError = (name: (keyof typeof allTableDataObject)[]): boolean => {
    setFieldsError({});

    const arrayCharacteristic = name.map((el) => {
      return {
        characteristicsTable: allTableDataObject[el],
      };
    });

    if (arrayCharacteristic.length > 0) {
      arrayCharacteristic.forEach((characteristicsTable) => {
        characteristicsTable.characteristicsTable.forEach((characteristicsTableEl) => {
          if (characteristicsTableEl.characteristic?.charType?.specificDicTypeId) {
            if (!characteristicsTableEl.specificDicId) {
              setFieldsError((prev) => ({
                ...prev,
                specificDicId: true,
              }));
            }
          }
          if (characteristicsTableEl.values?.length === 0) {
            setFieldsError((prev) => ({
              ...prev,
              values: true,
            }));
          }
          if (
            characteristicsTableEl.values?.length === 1 &&
            (characteristicsTableEl.values?.[0].value === undefined ||
              characteristicsTableEl.values?.[0].value === "" ||
              characteristicsTableEl.values?.[0].value === null)
          ) {
            setFieldsError((prev) => ({
              ...prev,
              values: true,
            }));
          }
          // if (
          //   getIsValidCardinality(characteristicsTableEl, characteristicsTable.characteristicsTable)
          // ) {
          //   setFieldsError((prev) => ({
          //     ...prev,
          //     values: true,
          //   }));
          // }
        });
      });
    }

    let isValid = true;

    setFieldsError((prev) => {
      if (prev.specificDicId || prev.values) {
        isValid = false;
      }

      return {
        ...prev,
      };
    });

    return isValid;
  };

  const [fromDateCharacteristic, setFromDateCharacteristic] = useState<string | null>();

  const onChangeFromDateCharacteristic = (fromDate?: Moment | null) => {
    setFromDateCharacteristic(fromDate ? toHttpFormatStartOfDay(fromDate) : undefined);
  };

  // Установка даты для загрузки доступных характеристик по умолчанию
  useEffect(() => {
    if (!fromDateCharacteristic && initialFromDate) {
      if (typeof initialFromDate === "string") {
        setFromDateCharacteristic(initialFromDate);
      } else {
        setFromDateCharacteristic(toHttpFormatStartOfDay(initialFromDate));
      }
    }
  }, [initialFromDate, fromDateCharacteristic]);

  // Загрузка справочников
  useEffect(() => {
    if (!checkIsLoaded(objectTypesLoading)) dispatch(fetchObjectTypes());
    if (!checkIsLoaded(characteristicsUnitsDicLoading)) dispatch(fetchCharacteristicsUnitsDic());
  }, []);

  // Загрузка словарных терминов для всех характеристик
  useEffect(() => {
    if (objectCharTypes?.length > 0 && !isLoading) {
      const arrayDicType: string[] = [];

      objectCharTypes.forEach((char) => {
        if (char?.charType?.specificDicTypeAlias) {
          arrayDicType.push(char?.charType?.specificDicTypeAlias);
        }

        if (char?.charType?.valueDicTypeAlias) {
          arrayDicType.push(char?.charType?.valueDicTypeAlias);
        }
      });

      if (arrayDicType?.length > 0) {
        dispatch(fetchCharacteristicDic([...new Set(arrayDicType)]));
        setIsLoading(true);
      }
    }
    return () => {
      setIsLoading(false);
    };
  }, [objectCharTypes]);

  // Сохраняем предыдущее значение даты (в самый первый раз)
  useEffect(() => {
    if (!prevFromDate && fromDateCharacteristic) {
      dispatch(characteristicsActions.setPrevFromDate(fromDateCharacteristic));
    }
  }, [fromDateCharacteristic, prevFromDate]);

  const getObjectCharTypes = (fromDateCharacteristic) => {
    dispatch(fetchObjectCharTypes({ onDate: fromDateCharacteristic }));
  };

  // Первоначальная загрузка характеристик
  useEffect(() => {
    if (fromDateCharacteristic && !objectCharTypesFromDate) {
      getObjectCharTypes(fromDateCharacteristic);
    }
  }, [fromDateCharacteristic, objectCharTypesFromDate]);

  // 1. Произошла первоначальная загрузка характеристик (есть objectCharTypesFromDate)
  // 2. Пользователь хочет изменить дату (меняется fromDateCharacteristic)
  // 3. Пользователь не менял дату (отсутствует prevFromDate)
  useEffect(() => {
    if (
      (allTableData.length === 0 || allTableData.length > 0) &&
      fromDateCharacteristic &&
      objectCharTypesFromDate &&
      !prevFromDate &&
      fromDateCharacteristic !== objectCharTypesFromDate
    ) {
      getObjectCharTypes(fromDateCharacteristic);
    }
  }, [objectCharTypesFromDate, fromDateCharacteristic]);

  // 1. Произошла первоначальная загрузка характеристик (есть objectCharTypesFromDate)
  // 2. Пользователь хочет изменить дату (меняется fromDateCharacteristic)
  // 3. У него есть характеристики (allTableData.length > 0)
  // 4. Пользователь менял дату (присутствует prevFromDate)
  useEffect(() => {
    if (
      allTableData.length > 0 &&
      fromDateCharacteristic &&
      prevFromDate &&
      fromDateCharacteristic !== prevFromDate
    ) {
      Modal.confirm({
        width: 500,
        title:
          "Список доступных характеристик может быть изменен. Вы хотите обновить данные о характеристиках?",
        onOk: () => {
          // Сбрасываем заполненные поля характеристик
          clearAllTableData();
          dispatch(
            characteristicsActions.setIsUpdateParams({
              isDisabled: false,
              isClearTable: true,
            })
          );
          getObjectCharTypes(fromDateCharacteristic);
          dispatch(characteristicsActions.setPrevFromDate(fromDateCharacteristic));
        },
        onCancel: () => {
          dispatch(
            characteristicsActions.setIsUpdateParams({
              isDisabled: true,
            })
          );
          dispatch(characteristicsActions.setPrevFromDate(fromDateCharacteristic));
        },
        okText: "Да",
        cancelText: "Нет",
      });
    }
  }, [fromDateCharacteristic, allTableData.length, prevFromDate]);

  // Если мы создаём новую версию то (запрашиваем характеристики на новую дату)
  const onExpandAddVersionForCharacteristics = (fromDate?: string) => {
    if (!fromDate) return;
    const dateSearch = moment(fromDate).add(1, "day");
    dispatch(characteristicsActions.setPrevFromDate(undefined));
    onChangeFromDateCharacteristic(dateSearch);
  };

  return {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    fieldsError,
    onCheckFieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    productCharValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
    setProductCharValuesCharacteristic,
    onExpandAddVersionForCharacteristics,
    onChangeExpandedRowKeysCharacteristic,
    expandedRowKeysCharacteristic,
  };
};
